import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';

import React, { useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FileContext } from '../../../context/FileContext';

const TableContainer = styled.div`
  overflow-x: auto;

  table {
    border: none;
    width: 100%;
    border-spacing: 0;
  }
  thead {
    background: ${({ color }) => color};
  }

  th,
  tr,
  td {
    box-shadow: none;
    text-align: left;
  }

  td {
    line-height: 1.2;
  }

  .resizer {
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    width: 5px;
    background: rgba(0, 0, 0, 0.5);
    cursor: col-resize;
    user-select: none;
    touch-action: none;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .resizer:hover {
    opacity: 1;
  }

  .resizer.isResizing {
    background: rgba(0, 0, 0, 0.8);
    opacity: 1;
  }
`;

const CustomTable = ({
  data = [],
  columns = [],
  lineItemData,
  isResizable = false,
  tableHeaderRef,
  lineItemId,
  getTable = () => {},
  onColumnResizeStart = () => {},
  onColumnResizeEnd = () => {},
  setColumnPercentages = () => {},
}) => {
  const { updateLineItemsRowHeights, isItemDragging } = useContext(FileContext);
  const [headerColor, setHeaderColor] = useState('#F5F6F8');
  const [isColumnResizing, setIsColumnResizing] = useState(false);

  const table = useReactTable({
    data,
    columns: useMemo(() => columns, [columns]),
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
  });

  const handleResizeStart = () => {
    if (!isColumnResizing) {
      setIsColumnResizing(true);
      onColumnResizeStart();
    }
  };

  const handleResizeEnd = () => {
    if (isColumnResizing) {
      setIsColumnResizing(false);
      onColumnResizeEnd();
    }
  };

  // Add global event listeners to handle mouse up outside the resize handle
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isColumnResizing) {
        handleResizeEnd();
      }
    };

    if (isColumnResizing) {
      document.addEventListener('mouseup', handleGlobalMouseUp);
      document.addEventListener('touchend', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('touchend', handleGlobalMouseUp);
    };
  }, [isColumnResizing]);

  useEffect(() => {
    // Get all visible leaf columns
    const columns = table.getVisibleLeafColumns();

    // Calculate total width
    const totalWidth = columns.reduce((sum, column) => {
      return sum + column.getSize();
    }, 0);

    // Calculate percentage for each column
    const percentages = columns.reduce((acc, column) => {
      const percentage = (column.getSize() / totalWidth) * 100;
      acc[column.id] = percentage;
      return acc;
    }, {});

    setColumnPercentages(percentages);
  }, [table.getState().columnSizing]); // Re-calculate when column sizing changes

  const tableSize = document.getElementById(
    `table-${lineItemId}`,
  )?.offsetHeight;
  useEffect(() => {
    updateLineItemsRowHeights(lineItemId, table);
  }, [lineItemId, tableSize, isItemDragging]);

  useEffect(() => {
    if (lineItemData?.header?.color) {
      setHeaderColor(lineItemData?.header?.color);
    }
  }, [lineItemData?.header?.color]);

  return (
    <TableContainer color={headerColor}>
      <table id={`table-${lineItemId}`}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr ref={tableHeaderRef} key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => {
                return (
                  <th
                    key={`${header.id}-${index}`}
                    colSpan={header.colSpan}
                    style={{
                      width: header.getSize(),
                      position: 'relative',
                    }}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                    <div
                      {...(isResizable && {
                        onMouseDown: (e) => {
                          handleResizeStart();
                          header.getResizeHandler()(e);
                        },
                        onTouchStart: (e) => {
                          handleResizeStart();
                          header.getResizeHandler()(e);
                        },
                        onMouseUp: handleResizeEnd,
                        onTouchEnd: handleResizeEnd,
                        onMouseLeave: handleResizeEnd,
                        className: `resizer ${
                          header.column.getIsResizing() ? 'isResizing' : ''
                        }`,
                      })}
                    />
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row, index) => (
            <tr
              key={`${row.id}-${index}`}
              id={`row-${lineItemId}-${row.id}${
                data.find((x) => x.id === Number(row.id))?.isEmpty
                  ? `-empty`
                  : ''
              }`}
            >
              {row.getVisibleCells().map((cell, index) => (
                <td
                  key={`${cell.id}-${index}`}
                  style={{
                    width: cell.column.getSize(),
                    ...(data.find((x) => x.id === Number(row.id)) && {
                      height: data.find((x) => x.id === Number(row.id))?.size,
                    }),
                  }}
                >
                  {lineItemData?.currency?.checked &&
                  lineItemData?.currency?.position?.value === 'before' &&
                  lineItemData?.currency?.column
                    .map((x) => x.value)
                    .includes(cell.column.id)
                    ? lineItemData?.currency?.type?.symbol
                    : null}
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  {lineItemData?.currency?.checked &&
                  lineItemData?.currency?.position?.value === 'after' &&
                  lineItemData?.currency?.column
                    .map((x) => x.value)
                    .includes(cell.column.id)
                    ? lineItemData?.currency?.type?.symbol
                    : null}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {getTable(table)}
    </TableContainer>
  );
};

export default CustomTable;
